import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getUserProfile, updateUserProfile } from "../../services/api";
import { salutations, countryCodes, usStates, researchAreas, countries } from "../../constants/types";

const Profile = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordRetype, setShowPasswordRetype] = useState(false);
  const [currentTab, setCurrentTab] = useState('personal');
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showPrimaryOtherRank, setShowPrimaryOtherRank] = useState(false);
  const [showSecondaryOtherRank, setShowSecondaryOtherRank] = useState(false);
  const [showSecondaryAffiliation, setShowSecondaryAffiliation] = useState(false);
  const navigate = useNavigate();

  const academicRanks = [
    "Professor",
    "Associate Professor", 
    "Assistant Professor",
    "Lecturer",
    "Senior Lecturer",
    "Instructor",
    "PhD Student",
    "Other"
  ];

  const [formData, setFormData] = useState({
    // Personal Information
    salutation: "",
    firstName: "",
    middleName: "",
    lastName: "",
    email: "",
    secondaryEmail: "",
    workPhone: "",
    workPhoneCountry: "+1",
    cellPhone: "",
    cellPhoneCountry: "+1",
    personalWebPage: "",

    // Primary Affiliation
    primaryAffiliation: {
      institution: "",
      rank: "",
      rankOther: "",
      department: "",
      address: {
        address1: "",
        address2: "",
        city: "",
        postalCode: "",
        country: "",
        state: ""
      }
    },

    // Secondary Affiliation
    secondaryAffiliation: {
      institution: "",
      rank: "",
      rankOther: "",
      department: "",
      address: {
        address1: "",
        address2: "",
        city: "",
        postalCode: "",
        country: "",
        state: ""
      }
    },

    // Areas of Expertise
    areasOfExpertise: {
      primary: ["", "", ""],
      secondary: ["", ""]
    },

    // Account Information
    username: "",
    currentPassword: "",
    newPassword: "",
    newPasswordRetype: "",
  });

  useEffect(() => {
    loadUserProfile();
  }, []);

  useEffect(() => {
    // Check if "Other" is selected for primary affiliation
    if (formData.primaryAffiliation?.rank === "Other") {
      setShowPrimaryOtherRank(true);
    } else {
      setShowPrimaryOtherRank(false);
    }

    // Check if "Other" is selected for secondary affiliation
    if (formData.secondaryAffiliation?.rank === "Other") {
      setShowSecondaryOtherRank(true);
    } else {
      setShowSecondaryOtherRank(false);
    }

    // Initialize secondary affiliation visibility based on form data
    const hasSecondaryData = formData.secondaryAffiliation?.institution || 
                            formData.secondaryAffiliation?.rank || 
                            formData.secondaryAffiliation?.department ||
                            formData.secondaryAffiliation?.address?.address1 ||
                            formData.secondaryAffiliation?.address?.city ||
                            formData.secondaryAffiliation?.address?.country;
    setShowSecondaryAffiliation(!!hasSecondaryData);
  }, [formData.primaryAffiliation?.rank, formData.secondaryAffiliation?.rank, formData.secondaryAffiliation]);

  const loadUserProfile = async () => {
    try {
      setLoading(true);
      const profile = await getUserProfile();
      
      // Transform the data to match form structure
      setFormData({
        ...profile,
        workPhoneCountry: profile.workPhoneCountry || "+1",
        cellPhoneCountry: profile.cellPhoneCountry || "+1",
        primaryAffiliation: profile.primaryAffiliation || {
          institution: "",
          rank: "",
          rankOther: "",
          department: "",
          address: {
            address1: "",
            address2: "",
            city: "",
            postalCode: "",
            country: "",
            state: ""
          }
        },
        secondaryAffiliation: profile.secondaryAffiliation || {
          institution: "",
          rank: "",
          rankOther: "",
          department: "",
          address: {
            address1: "",
            address2: "",
            city: "",
            postalCode: "",
            country: "",
            state: ""
          }
        },
        areasOfExpertise: profile.areasOfExpertise || {
          primary: ["", "", ""],
          secondary: ["", ""]
        },
        currentPassword: "",
        newPassword: "",
        newPasswordRetype: "",
      });
    } catch (error) {
      console.error("Error loading profile:", error);
      setError("Failed to load profile information");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name.includes('.')) {
      // Handle nested object updates
      const keys = name.split('.');
      setFormData(prev => {
        const updated = { ...prev };
        let current = updated;
        
        for (let i = 0; i < keys.length - 1; i++) {
          if (!current[keys[i]]) current[keys[i]] = {};
          current = current[keys[i]];
        }
        
        current[keys[keys.length - 1]] = value;
        
        // Clear "Other" field if rank is changed to something other than "Other"
        if (name.includes('rank') && !name.includes('rankOther')) {
          if (name.includes('primaryAffiliation') && value !== "Other") {
            updated.primaryAffiliation.rankOther = "";
          } else if (name.includes('secondaryAffiliation') && value !== "Other") {
            updated.secondaryAffiliation.rankOther = "";
          }
        }
        
        return updated;
      });
    } else if (name.includes('_') && (name.startsWith('primary_') || name.startsWith('secondary_'))) {
      // Handle areas of expertise (only fields with underscore and specific prefixes)
      const [type, index] = name.split('_');
      const idx = parseInt(index);
      
      setFormData(prev => ({
        ...prev,
        areasOfExpertise: {
          ...prev.areasOfExpertise,
          [type]: prev.areasOfExpertise[type].map((item, i) => i === idx ? value : item)
        }
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSecondaryAffiliationToggle = (e) => {
    const isChecked = e.target.checked;
    setShowSecondaryAffiliation(isChecked);
    
    // If unchecking, clear all secondary affiliation fields
    if (!isChecked) {
      setFormData(prev => ({
        ...prev,
        secondaryAffiliation: {
          institution: "",
          rank: "",
          rankOther: "",
          department: "",
          address: {
            address1: "",
            address2: "",
            city: "",
            postalCode: "",
            country: "",
            state: ""
          }
        }
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaveLoading(true);
    setError("");
    setSuccess("");

    try {
      // Validate password change if provided
      if (formData.newPassword) {
        if (!formData.currentPassword) {
          setError("Current password is required to change password");
          return;
        }
        if (formData.newPassword !== formData.newPasswordRetype) {
          setError("New passwords do not match");
          return;
        }
        if (formData.newPassword.length < 8) {
          setError("New password must be at least 8 characters long");
          return;
        }
      }

      await updateUserProfile(formData);
      setSuccess("Profile updated successfully!");
      
      // Clear password fields
      setFormData(prev => ({
        ...prev,
        currentPassword: "",
        newPassword: "",
        newPasswordRetype: "",
      }));
      
      // Scroll to top to show success message
      window.scrollTo({ top: 0, behavior: 'smooth' });
      
    } catch (err) {
      console.error('Profile update error:', err);
      if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError("Failed to update profile. Please try again.");
      }
    } finally {
      setSaveLoading(false);
    }
  };

  const createGroupedOptions = () => {
    const options = [];
    Object.keys(researchAreas).forEach(category => {
      options.push(
        <optgroup key={category} label={category}>
          {researchAreas[category].map(subcategory => (
            <option key={`${category}-${subcategory}`} value={subcategory}>
              {subcategory}
            </option>
          ))}
        </optgroup>
      );
    });
    return options;
  };

  const renderPersonalInfo = () => (
    <div className="card">
      <div className="card-header">
        <h5 className="mb-0">
          <i className="bi bi-person me-2"></i>
          Personal Information
        </h5>
      </div>
      <div className="card-body">
        <div className="row">
          <div className="col-md-6 mb-3">
            <label htmlFor="salutation" className="form-label">Salutation <span className="text-danger">*</span></label>
            <select
              className="form-select"
              id="salutation"
              name="salutation"
              value={formData.salutation}
              onChange={handleChange}
              required
            >
              <option value="">Select...</option>
              {salutations.map((sal) => (
                <option key={sal} value={sal}>{sal}</option>
              ))}
            </select>
          </div>
          <div className="col-md-6 mb-3">
            <label htmlFor="firstName" className="form-label">First Name <span className="text-danger">*</span></label>
            <input
              type="text"
              className="form-control"
              id="firstName"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              required
            />
          </div>
        </div>

        <div className="row">
          <div className="col-md-6 mb-3">
            <label htmlFor="middleName" className="form-label">Middle Name</label>
            <input
              type="text"
              className="form-control"
              id="middleName"
              name="middleName"
              value={formData.middleName}
              onChange={handleChange}
            />
          </div>
          <div className="col-md-6 mb-3">
            <label htmlFor="lastName" className="form-label">Last Name <span className="text-danger">*</span></label>
            <input
              type="text"
              className="form-control"
              id="lastName"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              required
            />
          </div>
        </div>

        <div className="row">
          <div className="col-md-6 mb-3">
            <label htmlFor="email" className="form-label">Primary Email <span className="text-danger">*</span></label>
            <input
              type="email"
              className="form-control"
              id="email"
              name="email"
              value={formData.email}
              readOnly
              disabled
              required
            />
            <div className="form-text text-muted">Primary email is locked because it is used for login credentials. Contact support to request a change.</div>
          </div>
          <div className="col-md-6 mb-3">
            <label htmlFor="secondaryEmail" className="form-label">Secondary Email</label>
            <input
              type="email"
              className="form-control"
              id="secondaryEmail"
              name="secondaryEmail"
              value={formData.secondaryEmail}
              onChange={handleChange}
            />
          </div>
        </div>

        <div className="row">
          <div className="col-md-6 mb-3">
            <label className="form-label">Work Phone</label>
            <div className="input-group">
              <select
                className="form-select"
                style={{ maxWidth: "100px" }}
                name="workPhoneCountry"
                value={formData.workPhoneCountry}
                onChange={handleChange}
              >
                {countryCodes.map((code) => (
                  <option key={code} value={code}>{code}</option>
                ))}
              </select>
              <input
                type="tel"
                className="form-control"
                name="workPhone"
                value={formData.workPhone}
                onChange={handleChange}
              />
            </div>
          </div>
          <div className="col-md-6 mb-3">
            <label className="form-label">Cell Phone</label>
            <div className="input-group">
              <select
                className="form-select"
                style={{ maxWidth: "100px" }}
                name="cellPhoneCountry"
                value={formData.cellPhoneCountry}
                onChange={handleChange}
              >
                {countryCodes.map((code) => (
                  <option key={code} value={code}>{code}</option>
                ))}
              </select>
              <input
                type="tel"
                className="form-control"
                name="cellPhone"
                value={formData.cellPhone}
                onChange={handleChange}
              />
            </div>
          </div>
        </div>

        <div className="mb-3">
          <label htmlFor="personalWebPage" className="form-label">Personal Web Page</label>
          <input
            type="url"
            className="form-control"
            id="personalWebPage"
            name="personalWebPage"
            value={formData.personalWebPage}
            onChange={handleChange}
            placeholder="http://"
          />
        </div>
      </div>
    </div>
  );

  const renderAffiliations = () => (
    <div className="card">
      <div className="card-header">
        <h5 className="mb-0">
          <i className="bi bi-building me-2"></i>
          Affiliations
        </h5>
      </div>
      <div className="card-body">
  <h6 className="text-primary mb-3">Primary Affiliation <small className="text-muted">(<span className="text-danger">*</span> required)</small></h6>
        
        <div className="row">
          <div className="col-md-6 mb-3">
            <label className="form-label">Institution <span className="text-danger">*</span></label>
            <input
              type="text"
              className="form-control"
              name="primaryAffiliation.institution"
              value={formData.primaryAffiliation?.institution || ''}
              onChange={handleChange}
              required
            />
          </div>
          <div className="col-md-6 mb-3">
            <label className="form-label">Academic Rank <span className="text-danger">*</span></label>
            <select
              className="form-select"
              name="primaryAffiliation.rank"
              value={formData.primaryAffiliation?.rank || ''}
              onChange={handleChange}
              required
            >
              <option value="">Select rank...</option>
              {academicRanks.map((rank) => (
                <option key={rank} value={rank}>{rank}</option>
              ))}
            </select>
            
            {showPrimaryOtherRank && (
              <div className="mt-2">
                <label className="form-label">Please specify other rank</label>
                <input
                  type="text"
                  className="form-control"
                  name="primaryAffiliation.rankOther"
                  value={formData.primaryAffiliation?.rankOther || ''}
                  onChange={handleChange}
                  placeholder="Please specify your rank"
                />
              </div>
            )}
          </div>
        </div>

        <div className="row">
          <div className="col-md-6 mb-3">
            <label className="form-label">Department</label>
            <input
              type="text"
              className="form-control"
              name="primaryAffiliation.department"
              value={formData.primaryAffiliation?.department || ''}
              onChange={handleChange}
            />
          </div>
          <div className="col-md-6 mb-3">
            <label className="form-label">Address 1 <span className="text-danger">*</span></label>
            <input
              type="text"
              className="form-control"
              name="primaryAffiliation.address.address1"
              value={formData.primaryAffiliation?.address?.address1 || ''}
              onChange={handleChange}
              required
            />
          </div>
        </div>

        <div className="row">
          <div className="col-md-4 mb-3">
            <label className="form-label">City <span className="text-danger">*</span></label>
            <input
              type="text"
              className="form-control"
              name="primaryAffiliation.address.city"
              value={formData.primaryAffiliation?.address?.city || ''}
              onChange={handleChange}
              required
            />
          </div>
          <div className="col-md-4 mb-3">
            <label className="form-label">Postal Code <span className="text-danger">*</span></label>
            <input
              type="text"
              className="form-control"
              name="primaryAffiliation.address.postalCode"
              value={formData.primaryAffiliation?.address?.postalCode || ''}
              onChange={handleChange}
              required
            />
          </div>
          <div className="col-md-4 mb-3">
            <label className="form-label">Country <span className="text-danger">*</span></label>
            <select
              className="form-select"
              name="primaryAffiliation.address.country"
              value={formData.primaryAffiliation?.address?.country || ''}
              onChange={handleChange}
              required
            >
              <option value="">Select Country...</option>
              {countries.map((country) => (
                <option key={country} value={country}>
                  {country}
                </option>
              ))}
            </select>
          </div>
        </div>

        {formData.primaryAffiliation?.address?.country === "United States" && (
          <div className="row">
            <div className="col-md-6 mb-3">
              <label className="form-label">State</label>
              <select
                className="form-select"
                name="primaryAffiliation.address.state"
                value={formData.primaryAffiliation?.address?.state || ''}
                onChange={handleChange}
              >
                <option value="">Select State...</option>
                {usStates.map((state) => (
                  <option key={state} value={state}>{state}</option>
                ))}
              </select>
            </div>
          </div>
        )}

        <hr className="my-4" />

        <div className="mb-3">
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              id="showSecondaryAffiliation"
              checked={showSecondaryAffiliation}
              onChange={handleSecondaryAffiliationToggle}
            />
            <label className="form-check-label" htmlFor="showSecondaryAffiliation">
              Enter Secondary Affiliation Details
            </label>
          </div>
          <small className="form-text text-muted">
            Check this box if you have a secondary institutional affiliation to add.
          </small>
        </div>

        {showSecondaryAffiliation && (
          <div>
            <h6 className="text-primary mb-3">Secondary Affiliation</h6>

            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">Institution</label>
                <input
                  type="text"
                  className="form-control"
                  name="secondaryAffiliation.institution"
                  value={formData.secondaryAffiliation?.institution || ''}
                  onChange={handleChange}
                />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">Academic Rank</label>
                <select
                  className="form-select"
                  name="secondaryAffiliation.rank"
                  value={formData.secondaryAffiliation?.rank || ''}
                  onChange={handleChange}
                >
                  <option value="">Select rank...</option>
                  {academicRanks.map((rank) => (
                    <option key={rank} value={rank}>{rank}</option>
                  ))}
                </select>
                
                {showSecondaryOtherRank && (
                  <div className="mt-2">
                    <label className="form-label">Please specify other rank</label>
                    <input
                      type="text"
                      className="form-control"
                      name="secondaryAffiliation.rankOther"
                      value={formData.secondaryAffiliation?.rankOther || ''}
                      onChange={handleChange}
                      placeholder="Please specify your rank"
                    />
                  </div>
                )}
              </div>
            </div>

            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">Department</label>
                <input
                  type="text"
                  className="form-control"
                  name="secondaryAffiliation.department"
                  value={formData.secondaryAffiliation?.department || ''}
                  onChange={handleChange}
                />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">Address 1</label>
                <input
                  type="text"
                  className="form-control"
                  name="secondaryAffiliation.address.address1"
                  value={formData.secondaryAffiliation?.address?.address1 || ''}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="row">
              <div className="col-md-4 mb-3">
                <label className="form-label">City</label>
                <input
                  type="text"
                  className="form-control"
                  name="secondaryAffiliation.address.city"
                  value={formData.secondaryAffiliation?.address?.city || ''}
                  onChange={handleChange}
                />
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label">Postal Code</label>
                <input
                  type="text"
                  className="form-control"
                  name="secondaryAffiliation.address.postalCode"
                  value={formData.secondaryAffiliation?.address?.postalCode || ''}
                  onChange={handleChange}
                />
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label">Country</label>
                <select
                  className="form-select"
                  name="secondaryAffiliation.address.country"
                  value={formData.secondaryAffiliation?.address?.country || ''}
                  onChange={handleChange}
                >
                  <option value="">Select Country...</option>
                  {countries.map((country) => (
                    <option key={country} value={country}>
                      {country}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {formData.secondaryAffiliation?.address?.country === "United States" && (
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label">State</label>
                  <select
                    className="form-select"
                    name="secondaryAffiliation.address.state"
                    value={formData.secondaryAffiliation?.address?.state || ''}
                    onChange={handleChange}
                  >
                    <option value="">Select State...</option>
                    {usStates.map((state) => (
                      <option key={state} value={state}>{state}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );

  const renderExpertise = () => (
    <div className="card">
      <div className="card-header">
        <h5 className="mb-0">
          <i className="bi bi-mortarboard me-2"></i>
          Areas of Expertise
        </h5>
      </div>
      <div className="card-body">
        <h6 className="text-primary mb-3">Primary Areas <span className="text-danger">*</span></h6>
        
        {[0, 1, 2].map((index) => (
          <div key={index} className="mb-3">
            <label className="form-label">Primary {index + 1} <span className="text-danger">*</span></label>
            <select
              className="form-select"
              name={`primary_${index}`}
              value={formData.areasOfExpertise?.primary?.[index] || ''}
              onChange={handleChange}
              required
            >
              <option value="">Select research area...</option>
              {createGroupedOptions()}
            </select>
          </div>
        ))}
      </div>
    </div>
  );

  const renderAccount = () => (
    <div className="card">
      <div className="card-header">
        <h5 className="mb-0">
          <i className="bi bi-gear me-2"></i>
          Account Settings
        </h5>
      </div>
      <div className="card-body">
        {/* <div className="mb-3">
          <label htmlFor="username" className="form-label">Username</label>
          <input
            type="text"
            className="form-control"
            id="username"
            name="username"
            value={formData.username}
            onChange={handleChange}
            required
          />
        </div> */}

        <hr className="my-4" />

        <h6 className="text-primary mb-3">Change Password</h6>
        <div className="alert alert-info">
          <i className="bi bi-info-circle me-2"></i>
          Leave password fields empty if you don't want to change your password.
        </div>

        <div className="mb-3">
          <label htmlFor="currentPassword" className="form-label">Current Password</label>
          <input
            type="password"
            className="form-control"
            id="currentPassword"
            name="currentPassword"
            value={formData.currentPassword}
            onChange={handleChange}
          />
        </div>

        <div className="mb-3">
          <label htmlFor="newPassword" className="form-label">New Password</label>
          <div className="input-group">
            <input
              type={showPassword ? "text" : "password"}
              className="form-control"
              id="newPassword"
              name="newPassword"
              value={formData.newPassword}
              onChange={handleChange}
            />
            <span className="input-group-text" style={{ cursor: 'pointer' }}>
              <i 
                className={`bi ${showPassword ? 'bi-eye-slash' : 'bi-eye'}`}
                onClick={() => setShowPassword(!showPassword)}
              ></i>
            </span>
          </div>
          <div className="form-text">Password should be at least 8 characters long.</div>
        </div>

        <div className="mb-3">
          <label htmlFor="newPasswordRetype" className="form-label">Confirm New Password</label>
          <div className="input-group">
            <input
              type={showPasswordRetype ? "text" : "password"}
              className="form-control"
              id="newPasswordRetype"
              name="newPasswordRetype"
              value={formData.newPasswordRetype}
              onChange={handleChange}
            />
            <span className="input-group-text" style={{ cursor: 'pointer' }}>
              <i 
                className={`bi ${showPasswordRetype ? 'bi-eye-slash' : 'bi-eye'}`}
                onClick={() => setShowPasswordRetype(!showPasswordRetype)}
              ></i>
            </span>
          </div>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="container py-5">
        <div className="d-flex justify-content-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container py-4">
      <div className="row">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h1>
              <i className="bi bi-person-circle me-2"></i>
              My Profile
            </h1>
            <button
              className="btn btn-outline-secondary"
              onClick={() => navigate('/dashboard')}
            >
              <i className="bi bi-arrow-left me-1"></i>
              Back to Dashboard
            </button>
          </div>

          {error && (
            <div className="alert alert-danger alert-dismissible fade show" role="alert">
              <i className="bi bi-exclamation-triangle me-2"></i>
              {error}
              <button
                type="button"
                className="btn-close"
                onClick={() => setError("")}
                aria-label="Close"
              ></button>
            </div>
          )}

          {success && (
            <div className="alert alert-success alert-dismissible fade show" role="alert">
              <i className="bi bi-check-circle me-2"></i>
              {success}
              <button
                type="button"
                className="btn-close"
                onClick={() => setSuccess("")}
                aria-label="Close"
              ></button>
            </div>
          )}

          <div className="row">
            <div className="col-lg-3">
              <div className="card">
                <div className="card-body">
                  <div className="nav flex-column nav-pills">
                    <button
                      className={`nav-link ${currentTab === 'personal' ? 'active' : ''}`}
                      onClick={() => setCurrentTab('personal')}
                    >
                      <i className="bi bi-person me-2"></i>
                      Personal Info
                    </button>
                    <button
                      className={`nav-link ${currentTab === 'affiliations' ? 'active' : ''}`}
                      onClick={() => setCurrentTab('affiliations')}
                    >
                      <i className="bi bi-building me-2"></i>
                      Affiliations
                    </button>
                    <button
                      className={`nav-link ${currentTab === 'expertise' ? 'active' : ''}`}
                      onClick={() => setCurrentTab('expertise')}
                    >
                      <i className="bi bi-mortarboard me-2"></i>
                      Areas of Expertise
                    </button>
                    <button
                      className={`nav-link ${currentTab === 'account' ? 'active' : ''}`}
                      onClick={() => setCurrentTab('account')}
                    >
                      <i className="bi bi-gear me-2"></i>
                      Password
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-lg-9">
              <form onSubmit={handleSubmit}>
                {currentTab === 'personal' && renderPersonalInfo()}
                {currentTab === 'affiliations' && renderAffiliations()}
                {currentTab === 'expertise' && renderExpertise()}
                {currentTab === 'account' && renderAccount()}

                <div className="d-flex justify-content-end mt-4">
                  <button
                    type="submit"
                    className="btn btn-primary btn-lg"
                    disabled={saveLoading}
                  >
                    {saveLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Saving...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-check-circle me-2"></i>
                        Save Changes
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;